import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea } from 'recharts';
import { PageHeader, SectionHeader, EmptyState, Field, Input, TYPE, cn } from './ui';
import { CHART, axisProps, tooltipStyle, tooltipLabelStyle } from './ui/chart';
import { Expense, Income, PayoffPlan, CreditCard, Loan } from '../types';
import { buildLedger } from '../utils/calculations';
import { addDays, parseLocalDate, today as todayFn, toDayKey } from '../utils/finance/dates';

interface BalanceProjectionProps {
  currentBalance: number;
  expenses: Expense[];
  incomes: Income[];
  plans: PayoffPlan[];
  creditCards: CreditCard[];
  loans?: Loan[];
  lowBalanceThreshold?: number;
}

export const BalanceProjection: React.FC<BalanceProjectionProps> = ({
  currentBalance,
  expenses,
  incomes,
  plans,
  creditCards,
  loans = [],
  lowBalanceThreshold = 0
}) => {
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [daysToProject, setDaysToProject] = useState(30);

  const days = useMemo(() => {
    const start = todayFn();
    return buildLedger({
      openingBalance: currentBalance,
      openingDate: start,
      from: start,
      to: addDays(start, daysToProject),
      expenses,
      incomes,
      loans,
      creditCards,
      plans,
      lowBalanceThreshold,
    });
  }, [currentBalance, expenses, incomes, plans, creditCards, daysToProject, loans, lowBalanceThreshold]);

  const selectedBalance = useMemo(() => {
    if (!selectedDate) return null;
    const key = toDayKey(parseLocalDate(selectedDate));
    return days.find(d => toDayKey(d.date) === key)?.balance ?? null;
  }, [selectedDate, days]);

  const minBalance = Math.min(...days.map(p => p.balance));
  const maxBalance = Math.max(...days.map(p => p.balance));

  // Prepare data for Recharts
  const chartData = days.map((p, index) => ({
    day: index,
    date: p.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    fullDate: p.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    balance: Math.round(p.balance),
    hasTransactions: p.events.length > 0,
    hasIncome: p.events.some(t => t.type === 'income'),
    hasExpense: p.events.some(t => t.type === 'expense')
  }));
  
  


  const usd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
  const endBalance = days[days.length - 1]?.balance ?? 0;
  const daysBelow = lowBalanceThreshold > 0
    ? days.filter(p => p.balance < lowBalanceThreshold).length
    : 0;

  return (
    <>
      <PageHeader
        heading="Balance Projection"
        subhead="Your checking balance day by day, from every scheduled income, expense and card payment."
      />

      {/* Each reading takes the colour of what it means: crimson only when the
          balance actually goes negative, ochre for time spent under the floor,
          money-in when the horizon ends up. */}
      <div className="mt-11 bg-surface border border-rule-base px-5 py-4 flex flex-wrap gap-x-12 gap-y-5">
        <div>
          <div className={cn(TYPE.sectionLabel, 'text-ink-muted')}>Lowest point</div>
          <div
            className={cn(
              TYPE.bigNumber,
              'mt-1.5',
              minBalance < 0
                ? 'text-bad'
                : lowBalanceThreshold > 0 && minBalance < lowBalanceThreshold
                  ? 'text-warn'
                  : 'text-ink'
            )}
          >
            {usd(minBalance)}
          </div>
        </div>
        <div>
          <div className={cn(TYPE.sectionLabel, 'text-ink-muted')}>
            {lowBalanceThreshold > 0 ? `Days below ${usd(lowBalanceThreshold)}` : 'Days below zero'}
          </div>
          <div className={cn(TYPE.bigNumber, 'mt-1.5', daysBelow > 0 ? 'text-warn' : 'text-ink')}>
            {daysBelow} of {daysToProject}
          </div>
        </div>
        <div>
          <div className={cn(TYPE.sectionLabel, 'text-ink-muted')}>In {daysToProject} days</div>
          <div
            className={cn(
              TYPE.bigNumber,
              'mt-1.5',
              endBalance < 0 ? 'text-bad' : endBalance > currentBalance ? 'text-money-in' : 'text-ink'
            )}
          >
            {usd(endBalance)}
          </div>
        </div>
      </div>

      <div className="mt-11 grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-10 gap-y-6 max-w-[640px]">
        <Field label="Projection period" hint="Days ahead, 7 to 365." htmlFor="bp-days">
          <Input
            id="bp-days"
            type="number"
            min="7"
            max="365"
            value={daysToProject}
            onChange={(e) => setDaysToProject(parseInt(e.target.value))}
          />
        </Field>
        <Field label="Check balance on date" hint="Optional — reads one day off the projection." htmlFor="bp-date">
          <Input
            id="bp-date"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </Field>
      </div>

      {selectedDate && selectedBalance !== null && (
        <div className="mt-7 border-l-2 border-accent pl-4">
          <div className={cn(TYPE.metaTight, 'text-ink-muted')}>
            Projected on {new Date(selectedDate).toLocaleDateString()}
          </div>
          <div className={cn(TYPE.bigNumber, 'mt-1.5', selectedBalance < 0 ? 'text-bad' : 'text-ink')}>
            {usd(selectedBalance)}
          </div>
        </div>
      )}

      <section className="mt-14">
        <SectionHeader
          label="Projected balance"
          note={lowBalanceThreshold > 0 ? `Threshold ${usd(lowBalanceThreshold)}` : undefined}
        />
        <div className="bg-surface border border-rule-base rounded-xl p-4 mt-5">
          <div className="h-64 sm:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 16, left: 4, bottom: 4 }}>
              <CartesianGrid stroke={CHART.grid} vertical={false} />
              <XAxis
                dataKey="day"
                type="number"
                domain={[0, chartData.length - 1]}
                tickFormatter={(day: number) => chartData[day]?.date ?? ''}
                ticks={chartData
                  .filter((_, i) => i % Math.max(1, Math.floor(chartData.length / 10)) === 0)
                  .map((d) => d.day)}
                {...axisProps}
              />
              <YAxis
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                domain={[
                  (dataMin: number) => Math.floor((dataMin - Math.abs(dataMin) * 0.05) / 1000) * 1000,
                  (dataMax: number) => Math.ceil((dataMax + Math.abs(dataMax) * 0.05) / 1000) * 1000,
                ]}
                {...axisProps}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                labelStyle={tooltipLabelStyle}
                itemStyle={{ color: CHART.ink }}
                cursor={{ stroke: CHART.axis }}
                formatter={(value: number) => [
                  typeof value === 'number' ? usd(value) : `$${value}`,
                  'Balance',
                ]}
                labelFormatter={(label, payload) => {
                  if (payload && payload.length > 0 && payload[0].payload && payload[0].payload.fullDate) {
                    return payload[0].payload.fullDate;
                  }
                  return '';
                }}
              />
              {/* Crimson marks zero and the region under it — the only place
                  the balance is actually wrong. */}
              {minBalance < 0 && (
                <ReferenceArea y1={minBalance} y2={0} fill={CHART.redFill} fillOpacity={1} />
              )}
              {minBalance < 0 && maxBalance > 0 && (
                <ReferenceLine y={0} stroke={CHART.red} />
              )}
              {/* The threshold is ochre, not red: below it is approaching
                  trouble, which is a different meaning from being overdrawn. */}
              {lowBalanceThreshold > 0 && minBalance < lowBalanceThreshold && (
                <ReferenceLine
                  y={lowBalanceThreshold}
                  stroke={CHART.warn}
                  strokeWidth={1.5}
                  strokeDasharray="6 5"
                  label={{
                    value: `LOW ${usd(lowBalanceThreshold)}`,
                    position: 'insideTopLeft',
                    fontSize: 10,
                    fontWeight: 500,
                    letterSpacing: '0.06em',
                    fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                    fill: CHART.warn,
                  }}
                />
              )}
              <Line
                type="linear"
                dataKey="balance"
                stroke={CHART.accent}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section className="mt-14">
        <SectionHeader label="Next 7 days" />
        {days.slice(0, 7).every(p => p.events.length === 0) ? (
          <EmptyState>Nothing scheduled in the next seven days</EmptyState>
        ) : (
          <div className="bg-surface border border-rule-base rounded-xl overflow-hidden mt-5">
            {days.slice(0, 7).map((p, i) => {
              if (p.events.length === 0) return null;
              return (
                <div key={i} className="px-4 py-4 border-b border-rule last:border-b-0">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
                    <span className={cn(TYPE.meta, 'text-ink')}>
                      {p.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </span>
                    <span className={cn(TYPE.meta, p.balance < 0 ? 'text-bad' : 'text-ink-muted')}>
                      Balance {usd(p.balance)}
                    </span>
                  </div>
                  <ul className="mt-2 space-y-1">
                    {p.events.map(t => (
                      <li key={t.id} className="flex items-baseline justify-between gap-6">
                        <span className="text-[13.5px] text-ink-soft min-w-0 truncate">{t.label}</span>
                        <span className="text-[13.5px] text-ink tabular-nums shrink-0">
                          {t.type === 'income' ? '+' : '\u2212'}{usd(t.amount)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            }).filter(Boolean)}
          </div>
        )}
      </section>
    </>
  );
};